import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';

export const ProjectContext = createContext();

/**
 * ProjectProvider
 * -------------------------------------------------
 * 1. Hydrates the last‑used project from localStorage so a refresh doesn’t reset selection.
 * 2. Watches the current route (/:projectId) – if present, loads that project and makes it active.
 * 3. Exposes setActiveProject() so any component (e.g. a dropdown) can switch projects at runtime.
 *    - Passing a valid project object persists it in localStorage.
 *    - Passing null clears selection and removes the cache (so you’re never “stuck”).
 */
export function ProjectProvider({ children }) {
  const { projectId } = useParams();

  // 1️⃣  hydrate last‑opened project (if any)
  const [activeProject, setActiveProjectState] = useState(() => {
    try {
      const cached = localStorage.getItem('activeProject');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  // Helper that both updates state AND maintains localStorage
  const setActiveProject = useCallback((proj) => {
    if (proj && proj._id) {
      localStorage.setItem('activeProject', JSON.stringify(proj));
    } else {
      localStorage.removeItem('activeProject');
    }
    setActiveProjectState(proj);
  }, []);

  // 2️⃣  If URL contains /:projectId, ensure that project is active
  useEffect(() => {
    if (!projectId) return; // No ID in route
    if (activeProject && activeProject._id === projectId) return; // Already loaded

    api.get(`/projects/${projectId}`)
       .then(res => setActiveProject(res.data))
       .catch(() => setActiveProject(null));
  }, [projectId, activeProject, setActiveProject]);

  // 3️⃣  Provide context downstream
  return (
    <ProjectContext.Provider value={{ activeProject, setActiveProject }}>
      {children}
    </ProjectContext.Provider>
  );
}
